/**
 * Block 3 (#1892) — the SEND HISTORY: what actually went out.
 *
 * A different thing from the target population next door, and the difference
 * is tense: every row here is a message that reached a real inbox, appended
 * only after the delivery resolved. The dedup table cannot answer this — it
 * stores STATE and drops the row on recovery — which is why this table had to
 * exist before per-account muting could, since muting destroys that state.
 *
 * The two money columns are AS OBSERVED AT SEND TIME and are the reason the
 * table exists at all. The question asked afterwards is "what did we know when
 * we decided this customer was over the line", and both quantities move
 * continuously — re-deriving them on read answers a different question every
 * time, and answers it wrongly for exactly the disputed case, the customer who
 * has since topped up.
 *
 * ── THE TABLE SAYS HOW MUCH OF THE RECORD IT IS SHOWING ─────────────────────
 * The read is capped. A capped list rendered without saying so is a silent
 * lie: an operator seeing a full page of rows will reasonably conclude that is
 * everything that was ever sent, and on THIS page — whose entire reason for
 * existing is to make "what actually happened" knowable — being wrong about
 * that is worse than not having shipped the page. So the footer distinguishes
 * "this is the complete record" from "this is the most recent N, and older
 * rows exist that you are not seeing". No pagination and no filters: the fix
 * is to stop misleading, not to invent a workflow nobody asked for.
 */
import type { AccountBalanceAlertHistoryEntry } from '@nadoutong/contracts';
import { formatLocalTimestamp, formatUsdCents } from '../formatters.ts';
import { useT } from '../../i18n/index.ts';
import { Chip, Table, TableEmpty, TBody, Td, Th, THead, Tr } from '../../ui/index.ts';
import { severityLabel, severityTone } from './balanceAlertStatus.ts';

interface Props {
  readonly entries: readonly AccountBalanceAlertHistoryEntry[];
  /** How many rows the read asked for — see the footer note below. */
  readonly limit: number;
}

export function BalanceAlertHistoryTable({ entries, limit }: Props): JSX.Element {
  const t = useT();
  // A full page back means the cap was reached, so older rows may exist. It
  // cannot distinguish "exactly `limit` were ever sent" from "more than
  // `limit`" — nothing in the response can — so the wording claims only what
  // is knowable: there MAY be older records.
  const capped = entries.length >= limit;
  return (
    <div className="flex flex-col gap-3">
      <p
        className="m-0 rounded border border-line-soft bg-well px-3 py-2 text-dense text-paper-mid"
        data-testid="balance-alerts-history-banner"
      >
        {t('balance_alerts.history.banner', { count: String(entries.length) })}
      </p>

      <Table testId="balance-alerts-history-table">
        <THead>
          <Tr interactive={false}>
            <Th>{t('balance_alerts.column.sent_at')}</Th>
            <Th>{t('balance_alerts.column.account')}</Th>
            <Th>{t('balance_alerts.column.message')}</Th>
            <Th num>{t('balance_alerts.column.spendable_at_send')}</Th>
            <Th num>{t('balance_alerts.column.days_left_at_send')}</Th>
            <Th>{t('balance_alerts.column.brand')}</Th>
          </Tr>
        </THead>
        <TBody>
          {entries.length === 0 ? (
            <TableEmpty colSpan={6}>{t('balance_alerts.history.empty')}</TableEmpty>
          ) : (
            entries.map((entry) => (
              <Tr key={entry.id} interactive={false} data-testid={`balance-alerts-history-row-${entry.id}`}>
                <Td mono>{formatLocalTimestamp(entry.sent_at, { seconds: true })}</Td>
                <Td emphasis>
                  <span className="flex flex-col">
                    <span>{entry.to_address}</span>
                    <span className="font-mono text-micro text-paper-low">{entry.account_id}</span>
                  </span>
                </Td>
                <Td>
                  <span className="flex flex-wrap items-center gap-1.5">
                    {/* `recovery` is the all-clear — a real delivery to a real
                        inbox, so it is recorded and shown like any other. Its
                        severity is the stored PEAK being cleared, which is why
                        a recovery row can legitimately read `critical` next to
                        a positive balance. */}
                    <Chip
                      tone={entry.kind === 'recovery' ? 'ok' : severityTone(entry.severity)}
                      testId={`balance-alerts-history-kind-${entry.id}`}
                    >
                      {entry.kind === 'recovery'
                        ? t('balance_alerts.history.kind_recovery', {
                            severity: t(severityLabel(entry.severity)),
                          })
                        : t(severityLabel(entry.severity))}
                    </Chip>
                    {entry.escalated ? (
                      <Chip tone="signal" lamp={false} testId={`balance-alerts-history-escalated-${entry.id}`}>
                        {t('balance_alerts.history.escalated')}
                      </Chip>
                    ) : null}
                  </span>
                </Td>
                <Td num mono>
                  {formatUsdCents(
                    entry.spendable_balance_usd_cents,
                    entry.spendable_balance_usd_cents < 0,
                  )}
                </Td>
                <Td num mono>
                  {entry.days_left === null ? '—' : entry.days_left}
                </Td>
                <Td mono>{entry.brand}</Td>
              </Tr>
            ))
          )}
        </TBody>
      </Table>

      {entries.length === 0 ? null : (
        <p
          className={
            capped
              ? 'm-0 rounded border border-warn-line bg-warn-dim/50 px-3 py-2 text-dense text-warn'
              : 'm-0 text-dense text-paper-low'
          }
          data-testid="balance-alerts-history-coverage"
        >
          {capped
            ? t('balance_alerts.history.coverage_capped', { count: String(entries.length) })
            : t('balance_alerts.history.coverage_complete', { count: String(entries.length) })}
        </p>
      )}
    </div>
  );
}
